import { DEFAULT_SETTINGS, type Settings } from './constants';
import { CARD_CATALOG } from './cards';
import type { DeckConfig } from './pack';

// ============================================================
// v29 BOLT-142 — settings that remember, device-local.
//
// Jake: "if a player ever edits a card or a game setting, that setting should be
// persisted for that user for future games that they create. I understand that
// there are no accounts, so this will be local to that client. To give you a
// specific example, if they change the number of radius cards from the default
// to let's say six, and then they end that game that day and then a week later
// create a new game, it should have that same radius six setting."
//
// And the precedence rule, in his words:
//
//   "Edited game settings should always take higher priority over client
//    preferences, and then client preferences should take preference over
//    global defaults."
//
//     edited game settings  >  client preferences  >  global defaults
//        (this room, now)       (this device)          (DEFAULT_SETTINGS)
//
// ## HOW THE ORDERING IS ENFORCED, and why it is enforced STRUCTURALLY
//
// Preferences are WRITTEN whenever the host edits, and READ EXACTLY ONCE — when
// a new game is created. They are never re-applied to a live room.
//
// That is not an implementation detail, it is the whole safety property. v22
// BOLT-103 is the cautionary tale this repo already paid for: a sticky resume
// token was allowed to outrank a deliberate Create/Join, and it hijacked QR
// joins for five tickets before anyone caught it. A preference that can be
// re-applied mid-session is the same bug wearing different clothes. Read-once
// means no re-render, reconnect, or mode change can stomp something the host
// just chose — there is no code path in which it could.
//
// Nothing here goes on the wire. There are no accounts (v22 made the team
// colour the only identity), so this is localStorage on web and UserDefaults on
// iOS, and `snapshotFor` is untouched.
// ============================================================

/** Bump when the stored shape changes incompatibly; older payloads are dropped. */
export const PREFS_VERSION = 1;

/**
 * The settings that persist.
 *
 * Deliberately a NAMED list rather than "everything in Settings": a room code,
 * a deck seed or a one-off test override must never follow the host into their
 * next game. `deckConfig` carries the per-card counts and cost overrides, which
 * is Jake's radius-count example.
 */
export const PERSISTED_SETTING_KEYS = [
  'travelMode',
  'deckConfig',
  'winCondition',
  'roundsPerTeam',
  'headStartMs',
  'maxRoundMs',
  'vetoWindowMs',
  'blockDurationMs',
  'photoWindowMs',
  'handCap',
  'foundRadiusFt',
] as const satisfies readonly (keyof Settings)[];

export type PersistedKey = (typeof PERSISTED_SETTING_KEYS)[number];
export type StoredPrefs = { version: number; settings: Partial<Settings> };

/** A stored payload that is structurally sound, or null. */
function parse(raw: string | null | undefined): StoredPrefs | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as StoredPrefs;
    if (!v || typeof v !== 'object') return null;
    if (v.version !== PREFS_VERSION) return null; // an older shape is dropped, not guessed at
    if (!v.settings || typeof v.settings !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

/**
 * Drop anything we would not want to restore into a new game.
 *
 * A stale or hostile payload must never be able to brick the lobby, so this is
 * total: unknown keys go, non-finite numbers go, non-positive durations go, and
 * a `deckConfig` entry naming a card that no longer exists goes. What survives
 * is a `Partial<Settings>` safe to merge over the defaults.
 */
export function sanitizePrefs(input: unknown): Partial<Settings> {
  const out: Partial<Settings> = {};
  if (!input || typeof input !== 'object') return out;
  const src = input as Record<string, unknown>;
  const known = new Set<string>(PERSISTED_SETTING_KEYS);
  const ids = new Set(CARD_CATALOG.map((c) => c.id));

  for (const [key, value] of Object.entries(src)) {
    if (!known.has(key)) continue; // never restore a key we did not choose to persist
    const fallback = (DEFAULT_SETTINGS as Record<string, unknown>)[key];

    if (key === 'deckConfig') {
      if (!value || typeof value !== 'object') continue;
      const cfg: DeckConfig = {};
      for (const [id, entry] of Object.entries(value as Record<string, unknown>)) {
        // A card id that no longer ships is dropped rather than carried
        // forward — this is how a renamed or retired card fails safe.
        if (!ids.has(id)) continue;
        if (!entry || typeof entry !== 'object') continue;
        const e = entry as { enabled?: unknown; count?: unknown; cost?: unknown };
        const clean: { enabled?: boolean; count?: number; cost?: number } = {};
        if (typeof e.enabled === 'boolean') clean.enabled = e.enabled;
        if (typeof e.count === 'number' && Number.isFinite(e.count) && e.count >= 0) clean.count = Math.floor(e.count);
        if (typeof e.cost === 'number' && Number.isFinite(e.cost) && e.cost >= 1) clean.cost = Math.floor(e.cost);
        if (Object.keys(clean).length) cfg[id] = clean as DeckConfig[string];
      }
      if (Object.keys(cfg).length) out.deckConfig = cfg;
      continue;
    }

    if (typeof fallback === 'number') {
      if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) continue;
      (out as Record<string, unknown>)[key] = value;
      continue;
    }
    if (typeof fallback === 'string') {
      if (typeof value !== 'string' || !value) continue;
      (out as Record<string, unknown>)[key] = value;
      continue;
    }
  }
  return out;
}

/** Keep only the persisted keys out of a full or partial settings object. */
export function pickPersisted(settings: Partial<Settings>): Partial<Settings> {
  const out: Partial<Settings> = {};
  for (const key of PERSISTED_SETTING_KEYS) {
    if (settings[key] === undefined) continue;
    (out as Record<string, unknown>)[key] = settings[key];
  }
  return out;
}

/**
 * Fold an edit into what is already stored.
 *
 * `set_settings` sends a PARTIAL, and `deckConfig` arrives one card at a time,
 * so a naive overwrite would forget every previously tuned card the moment the
 * host touched a different one.
 */
export function mergePrefs(stored: Partial<Settings>, edit: Partial<Settings>): Partial<Settings> {
  const next: Partial<Settings> = { ...stored, ...pickPersisted(edit) };
  if (edit.deckConfig) {
    next.deckConfig = { ...(stored.deckConfig ?? {}), ...edit.deckConfig };
  }
  return sanitizePrefs(next);
}

/**
 * THE PRECEDENCE RULE, as one function: what a NEW game should start from.
 *
 * `overrides` is anything the caller passed deliberately at create time (the
 * E2E harness's `__boltSettings`, a practice preset). It is the "deliberate
 * action" tier and outranks everything, which is the property that keeps this
 * from becoming BOLT-103 again.
 */
export function resolveCreateSettings(
  storedRaw: string | null | undefined,
  overrides?: Partial<Settings>,
): Partial<Settings> | undefined {
  const stored = sanitizePrefs(parse(storedRaw)?.settings);
  const merged: Partial<Settings> = { ...stored, ...(overrides ?? {}) };
  if (stored.deckConfig && overrides?.deckConfig) {
    merged.deckConfig = { ...stored.deckConfig, ...overrides.deckConfig };
  }
  // Nothing to say ⇒ say nothing, so the server keeps its own defaults rather
  // than receiving an empty object it has to reason about.
  return Object.keys(merged).length ? merged : undefined;
}

/** Serialize for storage. */
export function encodePrefs(settings: Partial<Settings>): string {
  return JSON.stringify({ version: PREFS_VERSION, settings: sanitizePrefs(settings) } satisfies StoredPrefs);
}

/** Read a stored payload back out, sanitized. */
export function decodePrefs(raw: string | null | undefined): Partial<Settings> {
  return sanitizePrefs(parse(raw)?.settings);
}
